import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Download, Share, Smartphone, X } from 'lucide-react';
import { Button } from '../ui/Button.jsx';
import {
  detectPlatform,
  dismissInstallPrompt,
  isIosDevice,
  shouldShowMobileInstallPrompt,
} from '../../features/install/utils.js';
import {
  promptPwaInstall,
  subscribePwaInstallPrompt,
} from '../../features/install/pwaInstall.js';
import { trackEvent } from '../../core/analytics/tracker.js';

const SHOW_DELAY_MS = 2500;

export function PwaInstallPrompt() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const platform = detectPlatform();
  const isIos = isIosDevice();

  useEffect(() => subscribePwaInstallPrompt(setDeferredPrompt), []);

  useEffect(() => {
    if (!shouldShowMobileInstallPrompt(location.pathname)) {
      setOpen(false);
      return undefined;
    }

    if (!isIos && !deferredPrompt) {
      setOpen(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setOpen(true);
      trackEvent({ category: 'PWA', action: 'InstallPromptShown' });
    }, SHOW_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [location.pathname, deferredPrompt, isIos]);

  const handleDismiss = () => {
    dismissInstallPrompt();
    setOpen(false);
    trackEvent({ category: 'PWA', action: 'InstallPromptDismissed' });
  };

  const handleInstall = async () => {
    const outcome = await promptPwaInstall();
    if (outcome === 'accepted') {
      trackEvent({ category: 'PWA', action: 'Installed' });
      setOpen(false);
      return;
    }

    if (outcome === 'dismissed') {
      dismissInstallPrompt();
      setOpen(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45 backdrop-blur-sm md:hidden">
      <button
        type="button"
        className="absolute inset-0"
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
      />

      <div className="modal-slide-up relative z-10 w-full max-w-lg rounded-t-[var(--radius-xl)] bg-gradient-to-b from-surface-card to-surface p-6 shadow-[var(--shadow-lg)]">
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-brand/5 min-h-0 min-w-0"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand/10">
          <Smartphone className="h-8 w-8 text-brand" />
        </div>

        <h2 className="text-center text-xl font-bold text-brand">Install Yocas</h2>
        <p className="mt-2 text-center text-[15px] leading-relaxed text-text-secondary">
          Add Yocas to your home screen for faster access, full-screen viewing, and a smoother
          mobile experience.
        </p>

        {isIos ? (
          <div className="mt-5 space-y-3 rounded-[var(--radius-md)] border border-brand/10 bg-white/70 p-4">
            <div className="flex items-start gap-3">
              <Share className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <p className="text-sm leading-relaxed text-text">
                Tap <span className="font-semibold">Share</span> in Safari, then choose{' '}
                <span className="font-semibold">Add to Home Screen</span>.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Download className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <p className="text-sm leading-relaxed text-text">
                Open Yocas from your home screen for the best experience.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-5 rounded-[var(--radius-md)] border border-brand/10 bg-white/70 p-4 text-sm leading-relaxed text-text">
            Install the app for quick access from your home screen and fewer browser distractions.
          </div>
        )}

        {!isIos && deferredPrompt && (
          <Button className="mt-5 w-full" onClick={handleInstall}>
            Install app
          </Button>
        )}

        <Button variant="ghost" className="mt-3 w-full" onClick={handleDismiss}>
          Not now
        </Button>

        {platform === 'samsung' && !deferredPrompt && (
          <p className="mt-3 text-center text-xs text-text-secondary">
            Use your browser menu and choose &quot;Add page to&quot; or &quot;Install app&quot;.
          </p>
        )}
      </div>
    </div>
  );
}
